import { createContext, useContext, useState, useEffect } from 'react';
import { DB_THEMES, DEFAULT_THEME, applyTheme, getSavedTheme } from '@/utils/themes';
import api from '@/utils/api';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  // Load theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // First try to get from localStorage for instant load
        const savedTheme = getSavedTheme();
        if (savedTheme && DB_THEMES[savedTheme]) {
          applyTheme(savedTheme);
          setCurrentTheme(savedTheme);
        }
        
        // Then try to sync with server
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const response = await api.get('/users/me');
            const userTheme = response.data?.theme || DEFAULT_THEME;
            if (userTheme !== savedTheme && DB_THEMES[userTheme]) {
              applyTheme(userTheme);
              setCurrentTheme(userTheme);
            }
          } catch (err) {
            // User not logged in, use localStorage theme
          }
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
        applyTheme(DEFAULT_THEME);
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Change theme function
  const changeTheme = async (themeId) => {
    if (!DB_THEMES[themeId]) return;
    
    applyTheme(themeId);
    setCurrentTheme(themeId);
    
    // Sync with server
    try {
      await api.put('/users/theme', { theme: themeId });
    } catch (error) {
      console.error('Failed to save theme to server:', error);
      // Still works locally via localStorage
    }
  };

  const value = {
    currentTheme,
    themes: DB_THEMES,
    changeTheme,
    loading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
